/**
 * B76B lane B — screen 1c, the full-screen athlete-surface boundary, and the
 * routing guard that serves it.
 *
 * ## The rule this exists to keep
 *
 * Handoff §1c: "The B76 server 403 must never reach the eye; the client routes
 * here first (same card, same two lines, everywhere)."
 *
 * Lane A's server refuses a coach on athlete endpoints with
 * `403 { error, roleBoundary: true }`. That refusal is the AUTHORIZATION and it
 * stays. What must never happen is a reader SEEING it: no error toast, no
 * empty athlete page that failed to load, no lock. The guard below renders the
 * boundary card BEFORE the athlete surface mounts, so on a coach account the
 * athlete endpoints are never called and there is no 403 to leak.
 *
 * ## Skeleton discipline
 *
 * Handoff §Interactions: "hold skeletons while the role fact loads — never
 * flash athlete UI at a coach or vice versa." The guard therefore renders a
 * neutral hold until the fact for THIS account has settled once, and only then
 * forks. It never renders `children` optimistically, which is the whole point:
 * an optimistic athlete render is exactly the flash the rule forbids.
 *
 * ⚖️ B47 fix 7 — the hold is a SKELETON, not a blank page. Handoff says hold
 * skeletons, and until now the guard held nothing at all: an empty cream div
 * where the route element used to be, TopBar and all (B134). It paints Today's
 * own skeleton now, with no name and no data of any account in it.
 *
 * `useAccountRole().coachOnly` degrades to the pre-migration composed signal
 * when the fetch fails, so a network blip cannot open an athlete surface to a
 * coach either.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccountRole } from '../../hooks/useAccountRole';
import { isDualArrivalArmed } from '../membership/DualArrivalBanner';
import { useAuthContext } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useOpenAthleteMembershipSheet } from '../../contexts/AthleteMembershipSheetContext';
import { ATHLETE_SURFACE_BOUNDARY } from '../../lib/coachAthletePaywallCopy';
// ⚖️ B47 fix 7 (B134's other half) — the frame the hold paints. Today's own
// skeletons, exported for this one caller; `App.tsx` already imports both this
// module and the route statically, so nothing about chunking moves.
import {
  CONTEXT_RETRY_ACTION_LABEL,
  DashboardSkeletonDesktop,
  DashboardSkeletonMobile,
} from '../../routes/today';
import { AthleteBoundary } from './AthleteBoundary';
import styles from './AthleteBoundary.module.css';

/** Screen 1c on its own — the card a coach gets on any athlete surface. */
export function AthleteSurfaceBoundary(): JSX.Element {
  const openSheet = useOpenAthleteMembershipSheet('athlete_surface_boundary');
  return (
    <AthleteBoundary
      variant="full"
      ariaLabel={ATHLETE_SURFACE_BOUNDARY.headline}
      kicker={ATHLETE_SURFACE_BOUNDARY.kicker}
      headline={ATHLETE_SURFACE_BOUNDARY.headline}
      body={ATHLETE_SURFACE_BOUNDARY.body}
      cta={ATHLETE_SURFACE_BOUNDARY.cta}
      onCta={openSheet}
      subLine={ATHLETE_SURFACE_BOUNDARY.subLine}
      backLink={{ label: ATHLETE_SURFACE_BOUNDARY.backLink, to: '/team' }}
    />
  );
}

/**
 * ⚖️ B47 fix 10 (round-52 critic F3) — WHAT MAKES THE HOLD'S FRAME UNREACHABLE.
 *
 * Fix 9 put `aria-hidden="true"` on the painted frame so the hold would say one
 * true thing instead of three wrong ones (B161's accessibility half). But
 * `aria-hidden` removes a subtree from the accessibility TREE and not from the
 * TAB ORDER, and this frame is Today's skeleton: `AppShell`'s skip link,
 * `SideNav`'s seven items, `QuickActions`' three links and `TopBar`'s three
 * buttons — eleven focusable elements, driven — so a keyboard or Switch Control
 * user tabbed straight into controls with no accessible node and heard nothing.
 * That is the `aria-hidden-focus` violation (WCAG 4.1.2): before fix 9 those
 * elements at least announced themselves.
 *
 * `inert` is the one attribute that answers both halves: the subtree leaves the
 * accessibility tree AND stops being focusable, clickable and findable.
 *
 * Two belts under it, because this ships to a WKWebView with a deployment
 * target of iOS 15.0 and `inert` landed in Safari 15.5:
 *
 *   · every focusable descendant is swept to `tabindex="-1"`, which is what
 *     keeps the frame out of the tab order where `inert` is not understood;
 *   · and where it is not understood, `aria-hidden` goes back on, so the frame
 *     is still out of the tree there. It is no longer the violation it was in
 *     fix 9, because by then nothing inside it is focusable.
 *
 * ⚠️ THE CONSEQUENCE, DELIBERATE: while the guard holds, nothing in the frame
 * can be clicked either, by anyone. It is a SKELETON — no name, no data, no
 * account — and B161's other half is precisely that a coach must not be able to
 * use live athlete quick actions inside it. The hold is bounded at 10 s on both
 * of the terms this arc could bound (B147, the auth boot, is the third).
 *
 * Written from an effect rather than as a prop because `inert` is not in
 * @types/react 18 and React 18 warns on a boolean unknown attribute — and
 * because the sweep cannot be a one-shot: the frame's own contents ARRIVE
 * AFTER IT MOUNTS. `TopBar` renders its three buttons only once the session
 * lands, so a ref callback swept a frame that did not have them yet (driven:
 * three reachable controls left at both tiers). The effect below carries no
 * dependency array, so every commit of the guard — the session landing is one —
 * re-sweeps whatever is in the frame now. Idempotent by construction.
 */
const HOLD_FRAME_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function sweepHoldFrame(node: HTMLDivElement | null): void {
  if (!node) return;
  node.setAttribute('inert', '');
  for (const el of Array.from(node.querySelectorAll<HTMLElement>(HOLD_FRAME_FOCUSABLE))) {
    el.setAttribute('tabindex', '-1');
  }
  if (!('inert' in HTMLElement.prototype)) node.setAttribute('aria-hidden', 'true');
}

/**
 * ⚖️ B250 fix round (UX 3, critic 2) — the two numbers the doubled-gap
 * cancellation below is built from. `RING_INSET` mirrors
 * `.ambiguousRetry::before`'s vertical `inset` (`AthleteBoundary.module.css`)
 * — the ring paints nothing of its own, but it is still "the control's box"
 * the target geometry is measured against. `RETRY_CARD_GAP` is what is left
 * between that ring and the first skeleton card once the doubled reservation
 * is cancelled: comfortably under the "about 24 px" ceiling, and never zero.
 */
const RING_INSET = 13;
const RETRY_CARD_GAP = 16;

/**
 * Client-side routing guard. Wrap any athlete surface (/today, /plan and its
 * deep links, /run) so a coach account gets 1c instead.
 *
 * Athlete and athlete_coach accounts render `children` byte-identical — this
 * guard adds no wrapper element on their path, so nothing about the athlete
 * app's layout or CSS changes.
 *
 * ⚖️ B294 (founder ruling 2026-09-29: go straight to Team) — every sign-in
 * lands on `/today`, so a coach-only account, and a coach removed from every
 * team above all, used to land on the athlete-side card and take a second tap
 * to reach the one place a coach works. On `/today` and nowhere else, a
 * SETTLED `coachOnly` now goes to `/team` with `replace` (Back does not bounce
 * into the redirect). `/plan`, its deep links and `/run` keep the card and its
 * "Back to your team" link exactly as they were. The redirect is the fork's
 * last branch, below the hold, so it can never fire during `boundaryPending`
 * or `ambiguousHold`: those frames still render exactly as before.
 *
 * ⚖️ B294 fix round (harvest critic F1, F2) — TWO ACCOUNTS ARE SPARED AND KEEP
 * THE CARD THEY HAD. `coachOnly` is also true for a coach who has BOUGHT
 * athlete training but not yet finished onboarding-lite (the server says
 * `athlete_coach`, the legacy row still says coach: the migration-window veto
 * in `useAccountRole.ts`), and for a coach whose crossing just armed the
 * arrival banner. Sending either to Team hides what they came for, and a
 * `<Navigate>` cannot be undone when the role later heals. So the redirect
 * also needs: NOT a resolved `athlete_coach`, and NOT an armed
 * `DualArrivalBanner` for this account. ⚠️ It is deliberately NOT narrowed to
 * `role === 'coach'`: while the server answers `athlete` for every account
 * (the migration window, and prod today) the legacy signal is the ONLY thing
 * that says coach-only, and that account is exactly who B294 is for.
 */
export function AthleteSurfaceGuard({ children }: { children: ReactNode }): JSX.Element {
  const { coachOnly, role, resolved, boundaryPending, ambiguousHold, refetch } = useAccountRole();
  const { user } = useAuthContext();
  // Router-relative (the basename is already stripped) and without the query
  // string. A hook, so it stays above every early return.
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const uid = user?.id ?? null;
  /** ⚖️ B47 fix 10 (round-52 critic F3) — the held frame, kept unreachable for
   *  as long as it is painted (see `sweepHoldFrame`). Null on every path that
   *  is not the hold, where the effect is a no-op. */
  const holdFrameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    sweepHoldFrame(holdFrameRef.current);
  });
  /**
   * ⚖️ B250 — THE RETRY CONTROL CLEARS THE FIXED TOP BAR.
   *
   * The mobile skeleton's `TopBar` is `position: fixed` (TopBar.module.css);
   * its in-flow spacer sibling reserves that height for the skeleton's OWN
   * content, but the B182 retry control renders outside `holdFrameRef` on
   * purpose and ahead of it in the DOM, so it started at the same flow
   * origin as the bar and sat painted UNDER it (measured 34..58 against a
   * 0..56 opaque bar, B250 — nothing of the label reached the eye).
   *
   * Measured at runtime, not hardcoded, for the same reason `TopBar` measures
   * its own spacer rather than a fixed token: a magic number drifts from the
   * real bar's height. `header` is the fixed bar's own element; its rendered
   * height is exactly the clearance the real spacer already gives every
   * other route. `DashboardSkeletonDesktop` renders no `header` (it uses
   * `AppShell`, not `TopBar`), so desktop measures 0 and stays untouched.
   *
   * ⚖️ B250 fix round (UX 3, critic 2) — THE DOUBLED GAP AND THE ROTATION.
   *
   * Padding the control down by the bar's height cleared the bar, but the
   * skeleton frame right below carries `TopBar`'s OWN spacer too — the same
   * clearance reserved a SECOND time, about 150 px of cream between the
   * control and the first card. `holdFrameRef`'s `marginTop` below cancels
   * exactly that second reservation, since both measurements read the same
   * real `header` and so always agree — leaving `RETRY_CARD_GAP` between the
   * control's box (ring included) and the first card, never zero, never
   * doubled. Cancelling the reservation puts the (now unpadded) frame's own
   * flow-top ABOVE the control's flow-bottom whenever the bar is taller than
   * the gap, and the frame's `.page` is `position: relative` — which paints
   * ABOVE static content regardless of DOM order (CSS 2.1's painting order,
   * positioned descendants over non-positioned ones) — so without help
   * `.page`'s own background would paint over the control's text in that
   * overlap. `.ambiguousRetryWrap` (`AthleteBoundary.module.css`) is that
   * help: its own `position: relative; z-index: 1` keeps the control
   * painting on top wherever the two boxes overlap. The fixed bar
   * (`z-index: 5`) still wins over both, and the two never actually touch —
   * the ring's own top sits 3 px below the bar's bottom edge either way,
   * unchanged by this fix round.
   *
   * And the measurement itself now runs off a `ResizeObserver` on the real
   * `header`, the same technique `TopBar.tsx` uses for its own spacer, not
   * only on the guard's next commit — so a rotation or a safe-area reflow
   * that changes the bar's height, with no other reason for this component
   * to re-render, still updates the clearance (and the cancelling margin
   * with it).
   */
  const [barClearance, setBarClearance] = useState(0);
  /** The last value actually committed to `barClearance`. A plain ref, not a
   *  read of `barClearance` itself, so the write below is skipped outright
   *  on an unchanged measurement rather than trusting a same-value `setState`
   *  to bail — this effect has no dependency array and runs on every commit
   *  the guard makes for any reason, so an un-skipped call here would re-run
   *  itself on its own re-render. */
  const measuredBarClearance = useRef(0);
  useLayoutEffect(() => {
    if (isDesktop) {
      if (measuredBarClearance.current !== 0) {
        measuredBarClearance.current = 0;
        setBarClearance(0);
      }
      return;
    }
    const bar = holdFrameRef.current?.querySelector('header');
    if (!bar) return;
    const measure = (): void => {
      const next = bar.getBoundingClientRect().height;
      if (measuredBarClearance.current !== next) {
        measuredBarClearance.current = next;
        setBarClearance(next);
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  });
  /**
   * ⚖️ B47 fix 4 (round-20 UX 2) — THE RE-HOLD IS SKIPPED AFTER A SETTLE…
   *
   * `boundaryPending` is `useAccountRole().loading || contextLoading`, so it
   * goes true again every time the athlete context RELOADS — and Today's own
   * "Try again" on the B47 held surface is exactly that call. The guard tore
   * the whole route down mid-retry: the athlete pressed the one control on the
   * page and got a cream page with only the bottom tab and the FAB, measured
   * at 6 to 13 s, then the surface returned mid-flight with its retry counter
   * reset to zero (a fresh mount cannot know a retry ever happened).
   *
   * ⚖️ B47 fix 5 (round-26 security 1 and critic F2) — …FOR THE ACCOUNT
   * WHOSE ROLE FACT IS IN HAND, AND FOR NO OTHER.
   *
   * Fix 4's licence for skipping the re-hold was "the role fact cannot change
   * while a context read goes around again". That is true of the SERVER fact
   * and false of everything else the boundary answer is made of:
   *
   *   · On the DEGRADED path (`/api/user/role` failed or answered a role this
   *     client does not recognise — `useAccountRole.ts:227`) the boundary
   *     answer IS the legacy composed signal, and `useCoachMode.ts:163`
   *     carries an explicit `!contextLoading` factor: `coachOnly` reads false
   *     for the WHOLE of any later reload, whoever is signed in. Skip the
   *     re-hold there and a coach-only account gets the athlete surface, then
   *     the 403 `roleBoundary` this guard exists to keep off the eye. So the
   *     re-hold stays until the role fact is `resolved`; degraded accounts
   *     keep the pre-fix-4 behaviour, which is a blank frame, never a leak.
   *
   *     🗑️ SUPERSEDED BY FIX 7, and the leak it names is still closed. The
   *     `!contextLoading` factor was the actual defect, and it is fixed in
   *     `useCoachMode` instead: the legacy answer is sticky for the uid it
   *     settled on, so the degraded coach reads coach-only right through his
   *     reload and never reaches the fork's athlete branch. The guard no
   *     longer needs `resolved`, and does not read it.
   *
   *   · A settle belongs to ONE ACCOUNT. `useAccountRole` never returns to
   *     `'pending'` on a `uid` change and the context provider keeps the
   *     previous user's `raw` through the new read (B131 owns both), so a
   *     cross-tab SIGNED_OUT → SIGNED_IN on a parked `/today`, or a
   *     `setSession` from a deep link, would otherwise render the athlete
   *     surface for the new account off the OLD account's answer. The latch is
   *     therefore the id it settled for, not a boolean.
   *
   * ⚠️ AND THE LATCH ONLY TAKES A SETTLE IT WATCHED ARRIVE (`pendingFor`).
   * A `uid` change lands one commit BEFORE the new account's reads start —
   * they begin in the context provider's effect — so on that commit nothing is
   * pending and a bare `settledFor.current = uid` would re-latch instantly on
   * the previous account's answer, leaving the id check dead for exactly the
   * transition it exists for. The id is recorded only when a pending frame for
   * THAT id has been seen, which every mount has (`useAccountRole` starts
   * `'pending'` on every mount, so a route-to-route remount still latches at
   * once and a later reload still skips the hold).
   *
   * What is skipped is still only the frame the re-hold never protected: a
   * reload for the SAME account whose role the server has already stated. The
   * FORK re-evaluates on every render either way — a reader who becomes
   * `coachOnly` gets the boundary on the next frame.
   */
  const settledFor = useRef<string | null | undefined>(undefined);
  const pendingFor = useRef<string | null | undefined>(undefined);
  if (boundaryPending) pendingFor.current = uid;
  if (!boundaryPending && pendingFor.current === uid) settledFor.current = uid;

  // Hold. Never `children`, never the boundary — we do not yet know which
  // reader is here, and guessing either way is a flash.
  //
  // ⚖️ B47 fix 7 (round-33 UX 1, critic F1) — AND NEVER AGAIN AFTER A SETTLE.
  //
  // Fix 5's `|| !resolved` re-held on every reload for any account whose
  // `/api/user/role` had failed, and `useAccountRole` had no way to ask again.
  // Today's "Try again" is the one non-mount caller of `reload()`, so on that
  // account every press unmounted the whole route: 8.2 s with no message, no
  // header and no button at 375, an empty `document.body.innerText` at 1440,
  // the escalation sentence unreachable because the component that counts
  // failures was destroyed, focus dropped to body. Five drives, all identical.
  //
  // What licensed that hold was `useCoachMode`'s `!contextLoading` factor —
  // the degraded boundary answer read "not a coach" for the whole of any
  // reload, whoever was signed in. That is now fixed where it lived: the
  // legacy answer is sticky for the uid it settled on (`useCoachMode.ts`), so
  // a degraded coach keeps his own surface across a reload and the guard no
  // longer has to blank everybody's page to protect him. The per-uid latch
  // below is untouched, and so is fix 6's ordering behaviour on a uid change:
  // a NEW account still has no settle of its own and still holds.
  if (boundaryPending && settledFor.current !== uid) {
    /**
     * ⚖️ B47 fix 7 (B134's other half) — THE HOLD PAINTS THE FRAME.
     *
     * This used to be an empty cream div. The guard is the route element
     * (`App.tsx:300`), so the TopBar unmounted with the page and the reader
     * had a blank window with nothing to read and no way out for the length
     * of the first settle. It now paints Today's own skeleton — the same one
     * the route shows while its reads run — with its TopBar and, on desktop,
     * the app shell.
     *
     * ⚠️ NOTHING OF ANY ACCOUNT IS IN IT. Both skeletons take the empty user
     * (`userInitial=""`, `{ initial: '', displayName: '', weekLabel: '' }`)
     * and no data of their own, which is what makes them safe on a hold that
     * covers an account CHANGE: there is no name, no initial and no row to
     * carry over from whoever was here before.
     */
    return (
      <div
        className={styles.hold}
        data-role-hold="true"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {/*
          ⚖️ B47 fix 9 (round-44 UX 4, critic F5 — B161's accessibility half) —
          ONE STATUS, ONE NAME.

          This region had no name of its own and every bit of its own content is
          decorative, so what it announced was the three nested `role="status"`
          regions the skeleton pieces carry: "Loading today's session",
          "Loading this week's plan", "Loading next race" — three live regions
          in one commit, wrong on the seven guarded routes that are not Today,
          and heard as nothing at all on VoiceOver (driven at 375: name "", zero
          characters of text). The frame leaves the accessibility tree and the
          region says one true thing instead. That also takes the rail's
          `aria-current="page"` on Today out of the tree, which is not where the
          reader is when `/plan` or `/run` is holding.

          The skeleton components are untouched: Today's own loading state still
          uses their regions, where those labels are accurate.

          ⚖️ B47 fix 10 (round-52 critic F3) — AND THE TOOL IS `inert`, NOT
          `aria-hidden`. See `holdFrame` above: `aria-hidden` took the frame out
          of the accessibility tree and left every control in it in the TAB
          ORDER — eleven of them, driven, the skip link first among them — so a
          keyboard or screen-reader user tabbed into controls that had no
          accessible node and heard nothing at all (WCAG 4.1.2,
          `aria-hidden-focus`). What the hold needs is a frame that is not
          reachable AT ALL while the tree hides it, which is what `inert` is.
        */}
        <span className={styles.srOnly}>Loading</span>
        {/*
          ⚖️ B182 (decision Y, 2026-09-23) — THE HOLD'S WAY OUT.

          `ambiguousHold` is true only for the one hold this file cannot heal
          by itself: a failed role fetch over a bare row (`useAccountRole.ts`,
          `holdOnAmbiguousFailure`). That hook now re-asks itself on a bounded
          backoff, but a reader should not have to wait it out in silence —
          this is the quiet manual door beside it, the "one press heals it"
          discipline the B47 retry surface already established, reused here
          rather than restated. It is placed OUTSIDE `holdFrameRef` on
          purpose: `sweepHoldFrame` marks that subtree `inert`, and this is
          the one control on the hold that must stay reachable — by mouse,
          keyboard and screen reader — while the rest of the frame stays
          exactly as unreachable as B47 fix 10 made it. `refetch()` is the
          same call the boundary's own hook instance would make: it drops the
          cached answer and issues a fresh request right now. It does NOT
          clear the two automatic re-asks the hold already scheduled at its
          own onset (`useAccountRole.ts`, `ambiguousHoldTimers` — 5 s, then
          15 s more) — a press adds a door, it does not cancel the other one;
          whichever settles the hold first wins (Opus sign-off 2026-09-23
          finding 5, correcting the earlier claim here).

          ⚖️ B244 — the 44px target: `styles.ambiguousRetry`
          (`AthleteBoundary.module.css`), an invisible pseudo-element ring
          around the link's own text rather than padding through it, so
          neither the text nor the row it sits in moves.

          ⚖️ B250 — the top padding carries `barClearance` (mobile only), the
          fixed bar's own measured height, so this box starts BELOW the bar
          instead of underneath it. See the effect above for why it is
          measured rather than a second hardcoded number.

          ⚖️ B250 fix round — `.ambiguousRetryWrap` (mobile only) keeps this
          box painting above the frame below once its own doubled reservation
          is cancelled (see the effect above and the `holdFrameRef` style
          below — same fix, both halves).
        */}
        {ambiguousHold && (
          <div
            className={!isDesktop ? styles.ambiguousRetryWrap : undefined}
            style={{ padding: `${16 + barClearance}px 20px 0` }}
          >
            <button
              type="button"
              className={`${styles.backLink} ${styles.ambiguousRetry}`}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
              onClick={refetch}
            >
              {CONTEXT_RETRY_ACTION_LABEL}
            </button>
          </div>
        )}
        <div
          ref={holdFrameRef}
          // ⚖️ B250 fix round — cancels the skeleton's own bar-height
          // reservation (see the effect above), leaving `RETRY_CARD_GAP`
          // above the first card instead of a second `barClearance`. Only
          // when the control is actually painted (`ambiguousHold`) and only
          // on mobile (`barClearance` is 0 on desktop either way, but there
          // is no control there to cancel a reservation for).
          style={
            !isDesktop && ambiguousHold
              ? { marginTop: `${RETRY_CARD_GAP + RING_INSET - barClearance}px` }
              : undefined
          }
        >
          {isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />}
        </div>
      </div>
    );
  }

  if (coachOnly) {
    // ⚖️ B294 — Today only. The trailing slash and the case are what the
    // router itself forgives when it matches the `/today` route. Fix round: not
    // for a resolved `athlete_coach` (a mid-purchase buyer) and not while the
    // dual arrival banner is armed for this account; both keep the card.
    if (
      /^\/today\/*$/i.test(pathname) &&
      !(resolved && role === 'athlete_coach') &&
      !isDualArrivalArmed(uid)
    ) {
      return <Navigate to="/team" replace />;
    }
    return <AthleteSurfaceBoundary />;
  }

  return <>{children}</>;
}
